/**
 * Resolve TipTap paragraph/heading block id at a document position.
 * Duck-typed so callers can pass ProseMirror / TipTap docs without shared TipTap deps.
 */

import { PARAGRAPH_BLOCK_TYPES } from "./chapterParagraphs";

export type BlockIdPosDoc = {
  content: { size: number };
  resolve: (pos: number) => {
    depth: number;
    node: (depth: number) => {
      type: { name: string };
      attrs?: { id?: string | null; [key: string]: unknown };
    };
  };
};

export function blockIdAtPos(doc: BlockIdPosDoc, pos: number): string | null {
  const size = doc.content.size;
  if (pos < 0 || pos > size) return null;
  try {
    const $pos = doc.resolve(Math.min(pos, size));
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
      const node = $pos.node(depth);
      if (!PARAGRAPH_BLOCK_TYPES.has(node.type.name)) continue;
      const id = node.attrs?.id;
      if (typeof id === "string" && id.trim()) return id.trim();
      return null;
    }
  } catch {
    return null;
  }
  return null;
}

/** Find block content range [from, to) for a paragraph id in a TipTap/PM doc. */
export function blockRangeById(
  doc: BlockIdPosDoc & {
    descendants: (
      f: (node: { type: { name: string }; attrs?: { id?: string | null }; nodeSize: number }, pos: number) => boolean | void
    ) => void;
  },
  blockId: string
): { from: number; to: number } | null {
  const want = blockId.trim();
  if (!want) return null;
  let found: { from: number; to: number } | null = null;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (!PARAGRAPH_BLOCK_TYPES.has(node.type.name)) return;
    const id = node.attrs?.id;
    if (typeof id === "string" && id.trim() === want) {
      found = { from: pos + 1, to: pos + node.nodeSize - 1 };
      return false;
    }
  });
  return found;
}
